/**
 * PublicChangelogAPI.js
 *
 * Cliente dos endpoints públicos (sem login) das Novidades da Gabriela.
 *
 * Endpoints (ver docs/features/changelog/CATALOGO-E-ENVIOS.md, "Público"):
 *   - public/changelog/entries.php      GET -> { success, entries: [...] } (só publicadas)
 *   - public/changelog/unsubscribe.php  POST { token, action: "unsubscribe" | "resubscribe" }
 *     -> { success, subscribed, email_hint } | { success: false, error }
 */

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://www.gabriela.app.br/api/';
const ENTRIES_URL = BASE_URL + 'public/changelog/entries.php';
const UNSUBSCRIBE_URL = BASE_URL + 'public/changelog/unsubscribe.php';

const CONNECTION_ERROR = {
  success: false,
  error: 'Erro de conexão. Verifique sua internet e tente novamente.',
};

/**
 * URL de mídia das Novidades pronta para o navegador. A API devolve o endereço do media.php
 * (absoluto, a partir da raiz ou relativo à API); no ambiente local a API está em outra porta.
 * @param {string|null} url
 * @returns {string|null}
 */
export const resolveChangelogMediaUrl = (url) => {
  if (!url) return null;
  try {
    return new URL(url, BASE_URL).toString();
  } catch (e) {
    return url;
  }
};

const resolveVideo = (video) => {
  if (!video) return null;
  if (typeof video === 'string') return resolveChangelogMediaUrl(video);
  return {
    ...video,
    src: resolveChangelogMediaUrl(video.src),
    poster: resolveChangelogMediaUrl(video.poster),
    captions: resolveChangelogMediaUrl(video.captions),
  };
};

const normalizeEntry = (entry) => ({
  ...entry,
  image: resolveChangelogMediaUrl(entry.image),
  video: resolveVideo(entry.video),
});

/**
 * Novidades publicadas, mais recentes primeiro (mesmo formato do antigo changelog.json).
 * @param {(res: { success: boolean, entries?: Array, error?: string }) => void} callback
 */
export const GetChangelogEntriesAPI = (callback) => {
  fetch(ENTRIES_URL, { method: 'GET' })
    .then((res) => res.json())
    .then((data) => {
      if (!data || data.success === false || !Array.isArray(data.entries)) {
        callback({ success: false, error: data?.error || 'Não foi possível carregar as novidades.' });
        return;
      }
      callback({ success: true, entries: data.entries.map(normalizeEntry) });
    })
    .catch(() => callback(CONNECTION_ERROR));
};

/**
 * Descadastra (ou recadastra) o usuário do token nas Novidades ou nas dicas de uso por e-mail.
 *
 * @param {string} token
 * @param {'unsubscribe'|'resubscribe'} action
 * @param {(res: { success: boolean, subscribed?: boolean, email_hint?: string, error?: string }) => void} callback
 * @param {'changelog'|'onboarding'} topic - assunto do link (o token de cada um é diferente)
 */
export const SetChangelogEmailSubscriptionAPI = (token, action, callback, topic = 'changelog') => {
  fetch(UNSUBSCRIBE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, action, topic }),
  })
    .then((res) => res.json())
    .then((data) => callback(data))
    .catch(() => callback(CONNECTION_ERROR));
};
